"use client"

import { useState, useEffect, useTransition } from "react"
import { Stack, Group, Text, NumberInput, Button, Alert, Anchor, Divider, Stepper } from "@forestsync/ui"
import { TbAlertCircle } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { prepareBrowseBuy, buyCreditsFromBrowse } from "@/actions/browse-buy"
import { cancelTradePaymentIntent } from "@/actions/trade-payment"

// Load Stripe.js once at module level (Stripe requirement)
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!)

const SLIPPAGE = 0.01
/** If freshly-fetched limitPrice differs from displayed estimate by more than this, show confirmation */
const PRICE_MOVED_THRESHOLD = 0.02

type Stage = "entry" | "confirm_price" | "payment" | "success"

interface PaymentData {
  limitPrice: number
  subtotal: number
  platformFee: number
  preAuthAmount: number
  clientSecret: string
  paymentIntentId: string
  creditType: string
  vintage: number
}

interface BuyerModePlacementPanelProps {
  projectRefId: string
  creditType: string
  vintage: number
  /** Live best ask from the real-time order book — used as the on-screen indicative price */
  bestAsk: number | null
  kycApproved?: boolean
}

interface BuyerPaymentFormProps {
  paymentData: PaymentData
  projectRefId: string
  volume: number
  onSuccess: (orderRef: string, filledVolume: number) => void
  onBack: () => void
}

function BuyerPaymentForm({ paymentData, projectRefId, volume, onSuccess, onBack }: BuyerPaymentFormProps) {
  const t = useTranslations("trading")
  const stripe = useStripe()
  const elements = useElements()
  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)

  function handleConfirmPay() {
    if (!stripe || !elements) return
    setError(null)
    startTransition(async () => {
      const confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      })
      if (confirmResult.error) {
        setError(confirmResult.error.message ?? t("genericError"))
        return
      }
      const piId = confirmResult.paymentIntent?.id ?? paymentData.paymentIntentId
      const result = await buyCreditsFromBrowse({
        projectRefId,
        volume,
        paymentIntentId: piId,
        limitPrice: paymentData.limitPrice,
        creditType: paymentData.creditType,
        vintage: paymentData.vintage,
      })
      if ("error" in result) {
        setError(result.error ?? t("genericError"))
        return
      }
      onSuccess(result.orderRef, result.filledVolume)
    })
  }

  return (
    <Stack gap="xs" p="xs">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{t("protectedPrice")}</Text>
        <Text size="sm" fw={600}>€{paymentData.limitPrice.toFixed(2)}/tCO₂e</Text>
      </Group>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{t("platformFee")}</Text>
        <Text size="sm">€{paymentData.platformFee.toFixed(2)}</Text>
      </Group>
      <Divider />
      <Group justify="space-between">
        <Text size="sm" fw={700}>{t("preAuthorized")}</Text>
        <Text size="sm" fw={700}>€{paymentData.preAuthAmount.toFixed(2)}</Text>
      </Group>
      <Text size="xs" c="dimmed">{t("estCostNote")}</Text>
      <Divider />
      <PaymentElement />
      {error && (
        <Alert color="red" py="xs" icon={<TbAlertCircle size={14} />}>
          <Text size="xs">{error}</Text>
        </Alert>
      )}
      <Button fullWidth color="green" loading={isPending} onClick={handleConfirmPay}>
        {t("confirmAndPay")}
      </Button>
      <Button fullWidth variant="subtle" color="gray" onClick={onBack}>
        {t("back")}
      </Button>
    </Stack>
  )
}

export function BuyerModePlacementPanel({ projectRefId, creditType, vintage, bestAsk, kycApproved = false }: BuyerModePlacementPanelProps) {
  const t = useTranslations("trading")
  const [stage, setStage] = useState<Stage>("entry")
  const [volume, setVolume] = useState<number | string>("")
  const [error, setError] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null)
  const [successData, setSuccessData] = useState<{ orderRef: string; filledVolume: number } | null>(null)
  // Track the ask price shown to the user at quote time (for staleness check)
  const [shownAsk, setShownAsk] = useState<number | null>(bestAsk)

  // Keep displayed ask in sync with live order book only while on entry stage
  useEffect(() => {
    if (stage === "entry") setShownAsk(bestAsk)
  }, [bestAsk, stage])

  const indicativePrice = shownAsk ? Math.round(shownAsk * (1 + SLIPPAGE) * 100) / 100 : null
  const qty = Number(volume)
  const indicativeTotal = indicativePrice && qty > 0 ? Math.round(indicativePrice * qty * 100) / 100 : null

  function handleContinue() {
    setError(null)
    if (!qty || qty <= 0) { setError(t("volumeRequired")); return }
    if (!bestAsk) { setError(t("noSellers")); return }

    startTransition(async () => {
      const result = await prepareBrowseBuy({ projectRefId, volume: qty, vintage, creditType })
      if ("error" in result) {
        setError(result.error === "NO_SELLERS" ? t("noSellers") : (result.error ?? t("genericError")))
        return
      }
      // Detect if freshly-fetched price moved significantly from what was shown
      const shownLimitPrice = Math.round((shownAsk ?? bestAsk!) * (1 + SLIPPAGE) * 100) / 100
      const priceMoved = Math.abs(result.limitPrice - shownLimitPrice) / shownLimitPrice > PRICE_MOVED_THRESHOLD
      setPaymentData(result)
      setStage(priceMoved ? "confirm_price" : "payment")
    })
  }

  async function handleBack() {
    if (paymentData?.paymentIntentId) {
      await cancelTradePaymentIntent({ paymentIntentId: paymentData.paymentIntentId }).catch(() => null)
    }
    setPaymentData(null)
    setStage("entry")
    setError(null)
  }


  // Derive stepper active step from verification state
  const activeStep = !kycApproved ? 0 : 1

  // ── ENTRY STAGE ──────────────────────────────────────────────────────────
  if (stage === "entry") {
    return (
      <Stack gap="xs" p="xs">
        {/* Upgrade progress stepper */}
        <Stepper active={activeStep} size="xs" py="xs">
          <Stepper.Step label={t("stepKyb")} />
          <Stepper.Step label={t("stepStripe")} />
          <Stepper.Step label={t("stepTrading")} />
        </Stepper>
        <Anchor href="/onboarding/trader" size="xs" fw={700} ta="center">
          {t("completeVerification")}
        </Anchor>

        {/* Live protected price */}
        {bestAsk ? (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{t("protectedPrice")}</Text>
            <Group gap={4} align="baseline">
              <Text size="sm" fw={600}>€{indicativePrice?.toFixed(2)}</Text>
              <Text size="xs" c="dimmed">/tCO₂e</Text>
            </Group>
          </Group>
        ) : (
          <Alert icon={<TbAlertCircle size={14} />} color="orange" variant="light" py="xs">
            <Text size="xs">{t("noSellers")}</Text>
          </Alert>
        )}

        <NumberInput
          label={t("volume")}
          placeholder="0"
          value={volume}
          onChange={setVolume}
          min={0}
          step={1}
          decimalScale={0}
          size="sm"
        />

        {indicativeTotal !== null && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{t("indicativeTotal")}</Text>
            <Text size="sm">~€{indicativeTotal.toFixed(2)}</Text>
          </Group>
        )}

        <Text size="xs" c="dimmed">{t("priceProtectionNote")}</Text>

        {error && (
          <Alert color="red" py="xs" icon={<TbAlertCircle size={14} />}>
            <Text size="xs">{error}</Text>
          </Alert>
        )}

        <Button fullWidth color="green" loading={isPending} disabled={!bestAsk} onClick={handleContinue}>
          {t("continueToPayment")}
        </Button>
      </Stack>
    )
  }

  // ── PRICE-MOVED CONFIRMATION ──────────────────────────────────────────────
  if (stage === "confirm_price" && paymentData) {
    return (
      <Stack gap="xs" p="xs">
        <Alert icon={<TbAlertCircle size={15} />} color="yellow" variant="light" py="xs">
          <Text size="sm" fw={500}>{t("priceMovedTitle")}</Text>
          <Text size="xs" c="dimmed">{t("priceMovedBody", { price: paymentData.limitPrice.toFixed(2) })}</Text>
        </Alert>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">{t("protectedPrice")}</Text>
          <Text size="sm" fw={600}>€{paymentData.limitPrice.toFixed(2)}/tCO₂e</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">{t("platformFee")}</Text>
          <Text size="sm">€{paymentData.platformFee.toFixed(2)}</Text>
        </Group>
        <Divider />
        <Group justify="space-between">
          <Text size="sm" fw={700}>{t("preAuthorized")}</Text>
          <Text size="sm" fw={700}>€{paymentData.preAuthAmount.toFixed(2)}</Text>
        </Group>
        <Button fullWidth color="green" onClick={() => setStage("payment")}>
          {t("continueAtNewPrice", { price: paymentData.limitPrice.toFixed(2) })}
        </Button>
        <Button fullWidth variant="subtle" color="gray" onClick={handleBack}>
          {t("back")}
        </Button>
      </Stack>
    )
  }

  // ── PAYMENT STAGE ─────────────────────────────────────────────────────────
  if (stage === "payment" && paymentData) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret: paymentData.clientSecret }}>
        <BuyerPaymentForm
          paymentData={paymentData}
          projectRefId={projectRefId}
          volume={qty}
          onBack={handleBack}
          onSuccess={(orderRef, filledVolume) => {
            setSuccessData({ orderRef, filledVolume })
            setStage("success")
          }}
        />
      </Elements>
    )
  }

  // ── SUCCESS STAGE ─────────────────────────────────────────────────────────
  if (stage === "success" && successData) {
    return (
      <Stack gap="xs" p="xs" align="center">
        <Text size="sm" fw={600} c="green">{t("orderPlaced")}</Text>
        <Text size="xs" c="dimmed" ta="center">
          {t("orderPlacedDesc", { ref: successData.orderRef, volume: successData.filledVolume })}
        </Text>
        <Button
          variant="subtle"
          size="xs"
          onClick={() => { setStage("entry"); setVolume(""); setSuccessData(null) }}
        >
          {t("back")}
        </Button>
      </Stack>
    )
  }

  return null
}
